import type { IKeyword } from "../../dashboard/types";
import { linkDaConsulta, rankingExibido, superficieDaPosicao } from "../../dashboard/pages/keyword-link";
import { dataCurta, diasEntre, paraData } from "./formatar";

const DIAS_ATE_ENVELHECER = 30;

export type LinhaDeBusca = {
  id: number;
  titulo: string;
  posicao: number;
  onde: string;
  medidaEm: string | null;
  antiga: boolean;
  /** Medida com data e não encontrada na lista — diferente de "ainda não medida". */
  foraDaLista: boolean;
  link: string | null;
};

export type ResumoDasPosicoes = {
  total: number;
  medidas: number;
  em1: number;
  top3: number;
  top10: number;
  /** Melhor posição medida (a primeira da lista ordenada), ou null sem medição. */
  melhor: LinhaDeBusca | null;
  linhas: LinhaDeBusca[];
};

const ONDE = { pesquisa: "Pesquisa do Google", maps: "Google Maps" } as const;

export const dataDaMedicao = (keyword: IKeyword): Date | null =>
  paraData(superficieDaPosicao(keyword) === "pesquisa" ? keyword.serpMeasuredAt : keyword.measuredAt);

export const linhaDeBusca = (keyword: IKeyword, hoje: Date = new Date()): LinhaDeBusca => {
  const data = dataDaMedicao(keyword);
  const posicao = rankingExibido(keyword);
  return {
    id: keyword.id,
    titulo: keyword.title,
    posicao,
    onde: ONDE[superficieDaPosicao(keyword)],
    medidaEm: dataCurta(data),
    antiga: data ? diasEntre(data, hoje) >= DIAS_ATE_ENVELHECER : false,
    foraDaLista: posicao < 1 && data !== null,
    link: linkDaConsulta(keyword),
  };
};

/**
 * Contagens honestas: só palavras que já foram medidas entram na conta, e a
 * lista sai das melhores posições para as piores, com as não medidas no fim.
 */
export const resumoDasPosicoes = (
  keywords: IKeyword[] | null | undefined,
  hoje: Date = new Date(),
): ResumoDasPosicoes => {
  const linhas = (keywords ?? []).map((keyword) => linhaDeBusca(keyword, hoje));
  const medidas = linhas.filter((linha) => linha.posicao >= 1);
  const ordenadas = [
    ...medidas.sort((a, b) => a.posicao - b.posicao),
    ...linhas.filter((linha) => linha.posicao < 1),
  ];
  return {
    total: linhas.length,
    medidas: medidas.length,
    em1: medidas.filter((linha) => linha.posicao === 1).length,
    top3: medidas.filter((linha) => linha.posicao <= 3).length,
    top10: medidas.filter((linha) => linha.posicao <= 10).length,
    melhor: ordenadas[0]?.posicao >= 1 ? ordenadas[0] : null,
    linhas: ordenadas,
  };
};

export const ultimaMedicao = (keywords: IKeyword[] | null | undefined): Date | null =>
  (keywords ?? [])
    .map(dataDaMedicao)
    .filter((data): data is Date => data !== null)
    .sort((a, b) => b.getTime() - a.getTime())[0] ?? null;
